import { useCallback, useContext, useState, useEffect } from 'react';
import { getExactKeyPath } from '../filterQueryFormat/logic/LogicGroup';
import MissingField from '../filterQueryFormat/MissingField';
import { isOperator, swapOperator } from '../filterQueryFormat/operator/Operator';
import SegmentationFiltersInstanceContext from '../state/SegmentationFiltersInstanceContext';
import { useFieldConditionChange } from './useFieldConditionChange';
import { useGetOperators } from './useGetOperators';
import { useStrategy } from './useStrategy';
import Raven from 'raven-js';
export const usePropertyChangedNameAndValueFilter = (filterFamily, condition, conditionPath) => {
  const {
    internalDraftValue,
    fetchMore
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    getOperators
  } = useGetOperators();
  const {
    getField
  } = useStrategy();
  const [hydratedCondition, setHydratedCondition] = useState(condition);
  useEffect(() => {
    let hydrated = condition;
    if (!hydrated.referencedObjectTypeId) {
      const eventParentLogicGroup = getExactKeyPath(conditionPath.pop().pop(), internalDraftValue);
      hydrated = hydrated.set('referencedObjectTypeId', eventParentLogicGroup.filterFamily);
    }
    const isPropertyValueMissing = hydrated.propertyName && !hydrated.propertyValue;
    const isPropertyValueFieldMissing = hydrated.propertyValue && isOperator(hydrated.propertyValue) && MissingField.isMissingField(hydrated.propertyValue.field);
    if (isPropertyValueMissing || isPropertyValueFieldMissing) {
      const loadField = async () => {
        const referencedPropertyField = (await getField(hydrated.referencedObjectTypeId, hydrated.propertyName)) || MissingField({
          name: hydrated.propertyName
        });
        let operators = getOperators(filterFamily, referencedPropertyField);
        if (!operators || operators.size < 1) {
          Raven.captureException(new Error(`[hydrated-condition-no-operator] No operators found for ${referencedPropertyField.name} property`));

          /**
           * @temporary Historically we used the referenced object type to fetch operators.
           * We're keeping this fallback while we confirm that it's safe to fetch operators using the event object type.
           * https://git.hubteam.com/HubSpot/customer-data-filters/pull/5782#discussion_r4143266
           * @expiration 2025-09-30
           * @deprecated
           */
          operators = getOperators(hydrated.referencedObjectTypeId, referencedPropertyField);
        }
        setHydratedCondition(hydrated.set('propertyValue',
        //@ts-expect-error operator types need improvements
        operators.first().of(referencedPropertyField)));
      };
      void loadField();
    } else {
      setHydratedCondition(hydrated);
    }
  }, [condition, conditionPath, internalDraftValue, getField, getOperators, filterFamily]);
  const {
    onFieldConditionValueChange
  } = useFieldConditionChange(filterFamily, conditionPath, hydratedCondition);
  const onPropertyNameChange = useCallback(evt => {
    const newCondition = evt.target.value;
    fetchMore({
      variables: {
        assetFamily: hydratedCondition.referencedObjectTypeId,
        ids: [newCondition.propertyName]
      }
    }).then(async () => {
      const selectedField = await getField(hydratedCondition.referencedObjectTypeId, newCondition.propertyName);
      if (!selectedField) {
        throw new Error(`Field not found for ${newCondition.propertyName} property`);
      }
      let operators = getOperators(filterFamily, selectedField);
      if (!operators || operators.size < 1) {
        Raven.captureException(new Error(`[on-property-name-change-no-operator] No operators found for ${selectedField.name} property`));

        /**
         * @temporary Historically we used the referenced object type to fetch operators.
         * We're keeping this fallback while we confirm that it's safe to fetch operators using the event object type.
         * https://git.hubteam.com/HubSpot/customer-data-filters/pull/5782#discussion_r4143266
         * @expiration 2025-09-30
         * @deprecated
         */
        operators = getOperators(hydratedCondition.referencedObjectTypeId, selectedField);
      }

      //@ts-expect-error types need improvements
      const valueCondition = operators.first().of(selectedField);
      onFieldConditionValueChange(newCondition.set('propertyValue', valueCondition), false);
    });
  }, [fetchMore, getField, getOperators, onFieldConditionValueChange, hydratedCondition.referencedObjectTypeId, filterFamily]);
  const onPropertyValueOperatorChange = useCallback(newOperator => {
    const newPropertyValueCondition = swapOperator(newOperator, hydratedCondition.propertyValue);
    onFieldConditionValueChange(hydratedCondition.set('propertyValue', newPropertyValueCondition), false);
  }, [hydratedCondition, onFieldConditionValueChange]);
  const onPropertyValueValueChange = useCallback(newPropertyValueCondition => {
    onFieldConditionValueChange(hydratedCondition.set('propertyValue', newPropertyValueCondition), true);
  }, [hydratedCondition, onFieldConditionValueChange]);
  return {
    hydratedCondition,
    onPropertyNameChange,
    onPropertyValueOperatorChange,
    onPropertyValueValueChange
  };
};